import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { PaymentComponentType } from '../types/types';
import { PowerBoardComponent } from './PowerBoardComponent';
import { useConfig } from '../../Common/hooks/useConfig';
import { Box, Checkbox, HStack, Image, Pressable, Text, VStack } from 'native-base';
import { AfterpayComponent } from './AfterpayComponent';
import { PaymentCard as PaymentCardComponent } from '../../Payment/components/PaymentCard';
import { TillComponent } from './TillComponent';
import PaymentEWay from './EwayComponent';
import currencyjs from 'currency.js';
import { isDoctorPortal } from '../../Common/utils/isDoctorPortal';
import {Icon} from "../../Common/components/Icon";
import {faArrowsRepeat} from "@fortawesome/pro-regular-svg-icons";

export const PaymentComponent =
  forwardRef((props: PaymentComponentType, ref) => {
    const { configForBrand } = useConfig();

    const config = configForBrand("leafdoctors");

    const [selectedMethod, setSelectedMethod] = useState<'card' | 'afterpay' | 'inperson'>(
      props.allowedMethods[0],
    );

    const [selectedSavedCard, setSelectedSavedCard] = useState<string | undefined>(
      props?.cards && props?.cards?.[0]?.id ? props?.cards?.[0]?.id : 'add',
    );

    useEffect(() => {
      if (selectedMethod === 'card' && props?.cards && props?.cards?.[0]?.id) {
        props.setCardCompleted(true);
      } else {
        if (selectedMethod === 'inperson')
          props.setCardCompleted(true);
        else
          props.setCardCompleted(false);
      }
    }, []);

    const onPressSelectedCard = (cardID: string) => {
      props.setCardCompleted(true);
      setSelectedSavedCard(cardID);
    };

    const nameForMethod = (name: string) => {
      if (name === 'afterpay') return 'Afterpay';
      if (name === 'inperson') return 'In Person (Cash)';
      return 'Card';
    };

    const equalPayments = currencyjs(props.total).divide(4);

    const afterpayTurnedOn = props?.allowedMethods?.includes('afterpay') && config?.afterpay;

    const filteredMethods = props.allowedMethods
      ?.filter((method) => {
        if (method === 'afterpay') return afterpayTurnedOn ?? false;
        if (!isDoctorPortal() && method === 'inperson') return false;
        return true;
      })
      .sort((a, b) => a.localeCompare(b));

    const innerRef = useRef(null);

    useImperativeHandle(
      ref,
      () => {
//        console.log('PaymentComponent - useImperativeHandle', selectedMethod, selectedSavedCard, config?.paymentGateway);
        return {
          confirmPayment: () => {
            //console.log('PaymentComponent - confirmPayment', selectedSavedCard);
            if (selectedMethod === 'card' && selectedSavedCard !== 'add') {
              const promise = new Promise((accept) => {
                const payload = JSON.stringify({
                  gateway: config?.paymentGateway,
                  type: 'token',
                  token: selectedSavedCard,
                });

                accept(payload);
              });

              return promise;
            } else {
              // console.log('PaymentComponent - useImperativeHandle is null ref', !innerRef?.current);
              return innerRef?.current?.confirmPayment();
            }
          }, getPaymentMethod: () => {
            // console.log('PaymentComponent - getPaymentMethod', selectedMethod);
            return selectedMethod;
          }
        };
      },
      [selectedMethod, selectedSavedCard],
    );

    return (
      <VStack bg={'white'} borderWidth={1} borderRadius={'md'} borderColor={'gray.200'}>
        {props.cards?.map((card) => (
          <PaymentCardComponent
            card={card}
            key={card?.id}
            savedCard={selectedMethod === 'card' ? selectedSavedCard : null}
            disabled={props.disabled}
            setSavedCard={(cardId) => {
              setSelectedMethod('card');
              onPressSelectedCard(cardId);
            }}
            setSelectedMethod={(method) => setSelectedMethod(method)}
            isSubscribeEligible={props.isSubscribeEligible}
          />
        ))}

        {filteredMethods?.map((method) => {
          const isSelected =
            method === 'card'
              ? selectedMethod === method && method === 'card' && selectedSavedCard === 'add'
              : selectedMethod === method;

          return (
            <VStack key={method}>
              <Pressable
                px={6}
                py={3}
                borderColor={'gray.200'}
                borderWidth={1}
                onPress={() => {
                  setSelectedMethod(method);

                  if (method === 'card') {
                    setSelectedSavedCard('add');
                  }
                }}
              >
                <HStack alignItems={'center'} space={2}>
                  <Checkbox value={''} isChecked={isSelected} />

                  <Text fontWeight={isSelected ? 600 : null}>{nameForMethod(method)}</Text>

                  {method === 'afterpay' && (
                    <Image
                      resizeMode={'contain'}
                      w={12}
                      h={6}
                      source={require('./afterpay-lockup-blackonmint.svg')}
                    />
                  )}

                  {method === 'card' && (
                    <>
                      <Image
                        borderRadius={'sm'}
                        w={12}
                        h={6}
                        source={require('../../Assets/PaymentMethods/1.png')}
                      />
                      <Image
                        borderRadius={'sm'}
                        w={12}
                        h={6}
                        source={require('../../Assets/PaymentMethods/2.png')}
                      />
                      <Image
                        borderRadius={'sm'}
                        w={12}
                        h={6}
                        source={require('../../Assets/PaymentMethods/22.png')}
                      />
                    </>
                  )}

                  <Box flex={1} />

                </HStack>
              </Pressable>

              {selectedMethod === method && method === 'card' && (
                <>
                  {selectedSavedCard === 'add' && (
                    <Box p={4} bg={'gray.50'}>
                      {config?.paymentGateway === 'eway' && (
                        <PaymentEWay ref={innerRef} {...props} />
                      )}

                      {config?.paymentGateway === 'till' && (
                        <TillComponent ref={innerRef} {...props} />
                      )}

                      {config?.paymentGateway === 'powerboard' && (
                        <PowerBoardComponent ref={innerRef} {...props} />
                      )}

                    </Box>
                  )}
                </>
              )}
              {selectedMethod === method && method === 'card' && props.isSubscribeEligible && isDoctorPortal() && (
                <Pressable
                  px={6}
                  py={3}
                  borderColor={'gray.200'}
                  borderWidth={1}
                  onPress={() => {
                    if (method === 'card') {
                      props.onSubscribingChange(!props.isSubscribing);
                    }
                  }}
                >
                  <HStack alignItems={'center'} space={2}>
                    <Checkbox value={''} isChecked={props.isSubscribing} />

                    <Text flex={1} minWidth={0}>
                      Orders will be generated automatically for this customer's refills on the refill interval.
                    </Text>
                  </HStack>
                </Pressable>
              )}

              {selectedMethod === method && method === 'afterpay' && (
                <AfterpayComponent ref={innerRef} {...props} />
              )}
            </VStack>
          );
        })}
      </VStack>
    );
  });
